import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import axios from 'axios';
import { toast } from 'react-toastify';
import { StockItem } from './core/_models';
import StockReportTableRow from './StockReportTableRow';
import SharedData from '../../common/SharedData';
import {API_URL} from '../../../tenant/apiBase';

type Props = {
    data: StockItem[];
    isLoading: boolean;
    totalItems: number;
    refreshData: () => void;
};

const StockReportTable: React.FC<Props> = ({ data, isLoading, totalItems, refreshData }) => {
    const { formatMessage } = useIntl();
    const { currentUser, language } = SharedData();
    const [isAddingAll, setIsAddingAll] = useState<boolean>(false);
    
    // A partner shop's own shelf (store_type SHOP) has nothing to return: no Action column and no
    // "add all" when every row is one — only stock held with us is returnable.
    const returnable = data.filter(item => item.store_type !== 'SHOP');
    const showAction = returnable.length > 0;
    const hasItemsToAdd = returnable.some(item => item.renw_return == null);

    const handleAddAllToGRR = async () => {
        if (!currentUser?.token || data.length == 0) return;
        
        setIsAddingAll(true);
        try {
            const promises = returnable.map(item => {
                if (item.renw_return != null) return Promise.resolve();
                
                return axios.post(
                    `${API_URL}/api/partner-portal/return-po/add-product-to-return-po`,
                    {
                        product_supplier_quantity_id: item.product_supplier_quantity_id,
                        quantity: item.quantity
                    },
                    {
                        headers: {
                            token: currentUser.token,
                            language: language
                        }
                    }
                );
            });
            
            await Promise.all(promises);
            
            toast.success(formatMessage({ id: 'STOCK.ADD_ALL_TO_GRR_SUCCESS' }) || 'All products added to GRR successfully', {
                onClose: () => {
                    refreshData();
                }
            });
        } catch (error) {
            toast.error(formatMessage({ id: 'STOCK.ADD_ALL_TO_GRR_ERROR' }) || 'Failed to add all products to GRR');
        } finally {
            setIsAddingAll(false);
        }
    };

    return (
        <>
            <table className='table align-middle gs-0 gy-5'>
                <thead>
                   
                    <tr>
                        <th className="fw-bold p-0 px-2 text-nowrap text-uppercase">{formatMessage({ id: 'TABLE.HEADER.IMAGE' })}</th> 
                        <th className="fw-bold p-0 px-2 text-uppercase">{formatMessage({ id: 'TABLE.HEADER.NAME' })}</th>
                        <th className="fw-bold p-0 px-2 text-nowrap text-uppercase">{formatMessage({ id: 'TABLE.HEADER.MODEL' })}</th>
                        <th className="fw-bold p-0 px-2 text-uppercase">{formatMessage({ id: 'TABLE.HEADER.BRAND' })}</th>
                        <th className="fw-bold p-0 px-2 text-nowrap text-uppercase">{formatMessage({ id: 'TABLE.HEADER.BARCODE' })}</th>
                        <th className="fw-bold p-0 px-2 text-nowrap text-uppercase">{formatMessage({ id: 'TABLE.HEADER.STORE' })}</th>
                        <th className="fw-bold p-0 px-2 text-nowrap text-uppercase">{formatMessage({ id: 'TABLE.HEADER.EXPIRY_DATE' })}</th>
                        <th className="fw-bold p-0 px-2 text-nowrap text-uppercase">{formatMessage({ id: 'TABLE.HEADER.PO_ID' })}</th>
                        <th className="fw-bold p-0 px-2 text-nowrap text-uppercase text-end">{formatMessage({ id: 'TABLE.HEADER.QUANTITY' })}</th>
                        {showAction && (
                            <th className="fw-bold p-0 px-2 text-nowrap text-uppercase text-center">{formatMessage({ id: 'TABLE.HEADER.ACTION' })}</th>
                        )}
                    </tr>
                </thead>
                <tbody>

                    {data.map((item, index) => (
                        <StockReportTableRow
                            key={`${item.barcode}-${item.po_id}-${index}`}
                            item={item}
                            refreshData={refreshData}
                            showAction={showAction}
                        />
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={6} className='fw-bold text-end pe-3'>
                            {formatMessage({ id: 'TABLE.FOOTER.TOTAL_ITEMS' })}:
                        </td>
                        <td className='fw-bold text-end'>
                            {totalItems}
                        </td>
                        {showAction && <td className='text-center'>
                            {hasItemsToAdd && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-dark"
                                    onClick={handleAddAllToGRR}
                                    disabled={isAddingAll || data.length == 0}
                                >
                                    {isAddingAll ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ) : (
                                        formatMessage({ id: 'STOCK.ADD_ALL_TO_GRR' })
                                    )}
                                </button>
                            )}
                        </td>}
                    </tr>
                </tfoot>
            </table>
        </>
    );
};

export default StockReportTable;
